/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

// Ported from light-grid-wapp app/components/home/EmptyState.tsx: the same icon, title,
// description, and optional action, on a dashed surface that reads as reserved space.

import type { ReactNode } from "react";
import { Inbox, SearchX } from "lucide-react";
import { Button } from "./Button";
import { cn } from "../../lib/utils";

export type EmptyStateTone = "quiet" | "search";

const TONE_ICONS: Record<EmptyStateTone, ReactNode> = {
  quiet: <Inbox size={20} />,
  search: <SearchX size={20} />,
};

export function EmptyState({
  icon,
  title,
  description,
  tone = "quiet",
  actionLabel,
  onAction,
  className,
}: {
  icon?: ReactNode | undefined;
  title: string;
  description: string;
  tone?: EmptyStateTone | undefined;
  actionLabel?: string | undefined;
  onAction?: (() => void) | undefined;
  className?: string | undefined;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "enter flex flex-col items-center gap-3 rounded-3xl border border-dashed border-border/70 px-6 py-10 text-center",
        className,
      )}
    >
      <span
        aria-hidden
        className="grid size-10 place-items-center rounded-full bg-bg-hover/70 text-text-tertiary"
      >
        {icon ?? TONE_ICONS[tone]}
      </span>
      <div className="space-y-1">
        <p className="font-display text-lg leading-tight text-text-primary">
          {title}
        </p>
        <p className="max-w-sm text-sm leading-relaxed text-text-tertiary">
          {description}
        </p>
      </div>
      {actionLabel && onAction && (
        <Button variant="ghost" size="sm" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
